import { useCallback, useEffect, useState } from "react"

import { TransportRequestError } from "@/transport"

import type { ApplicationAccessResponse } from "@/types_api/backend/transport/application_access_response"

import {
  readApplicationAdmission,
  type AdmissionScope,
} from "./applicationAdmissionTransport"

type AdmissionState =
  | { status: "checking" }
  | { status: "unavailable" }
  | { status: "unprovisioned" }
  | { status: "ready"; decision: ApplicationAccessResponse }

// Includes token minting and transport so neither can leave entry spinning.
const ENTRY_TIMEOUT_MS = 10_000

export function useApplicationAdmission({
  scope,
  sessionId,
  getToken,
}: {
  scope: AdmissionScope | null
  sessionId: string | null
  getToken: Parameters<typeof readApplicationAdmission>[0]["getToken"]
}) {
  const scopeKey = JSON.stringify([sessionId, scope])
  const [attempt, setAttempt] = useState(0)
  const [result, setResult] = useState<{
    key: string
    state: AdmissionState
  } | null>(null)
  const retry = useCallback(() => setAttempt((value) => value + 1), [])
  const userId = scope?.userId ?? null
  const organizationId = scope?.organizationId ?? null
  const organizationSlug = scope?.organizationSlug ?? null

  useEffect(() => {
    if (!userId || !sessionId) {
      return
    }
    const controller = new AbortController()
    const finish = (state: AdmissionState) => {
      if (!controller.signal.aborted) {
        setResult({ key: scopeKey, state })
      }
    }
    const timer = window.setTimeout(() => {
      finish({ status: "unavailable" })
      controller.abort()
    }, ENTRY_TIMEOUT_MS)
    void readApplicationAdmission({
      scope: { userId, organizationId, organizationSlug },
      getToken,
      signal: controller.signal,
    }).then(
      (decision) => {
        window.clearTimeout(timer)
        finish({ status: "ready", decision })
      },
      (error: unknown) => {
        window.clearTimeout(timer)
        finish({
          status:
            error instanceof TransportRequestError &&
            error.status === 403 &&
            error.detail?.code === "user_not_mapped"
              ? "unprovisioned"
              : "unavailable",
        })
      },
    )
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [
    attempt,
    getToken,
    organizationId,
    organizationSlug,
    scopeKey,
    sessionId,
    userId,
  ])

  useEffect(() => {
    window.addEventListener("focus", retry)
    return () => window.removeEventListener("focus", retry)
  }, [retry])

  // A same-scope refresh retains mounted state until the fresh decision lands.
  // Identity or organization changes still withhold the old tree immediately.
  const state: AdmissionState =
    scope && result?.key === scopeKey ? result.state : { status: "checking" }
  return { state, retry }
}
